import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from 'react-query';

import { Box } from '@mui/material';
import Close from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DoneIcon from '@mui/icons-material/Done';
import ChatIcon from '@mui/icons-material/Chat';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Spinner from 'components/Spinner/Spinner';
import BodyText from 'components/Typography/BodyText/BodyText';
import BuyerTabSectionsWrapper from 'components/Wrappers/TabSectionsWrapper/BuyerTabSectionsWrapper/BuyerTabSectionsWrapper';
import ModalBriefProposalComplete from 'components/ModalBriefProposalComplete/ModalBriefProposalComplete';
import ModalCongratulations from 'components/ModalCongratulations/ModalCongratulations';
import TopBarTitleStatus from 'components/TopBarTitleStatus/TopBarTitleStatus';
import ModalConfirmAccept from 'components/ModalConfirmAccept/ModalConfirmAccept';
import ModalAcceptProposal from 'components/ModalAcceptProposal/ModalAcceptProposal';
import ModalRejectBrief from 'components/ModalRejectBrief/ModalRejectBrief';
import ScrollableContainer from 'components/ScrollableContainer/ScrollableContainer';
import ChatBox from 'components/ChatBox/ChatBox';

import { routes } from 'options/routes';
import { PROJECT_STATUS } from 'options/projectStatus';
import { PROPOSAL_STATUS } from 'options/proposalStatus';

import { capitalize } from 'utils/capitalize';
import {
  getIsAgreementTabDisabled,
  getIsFilesTabDisabled,
  getIsProposalTabDisabled,
  getShowCheckoutButton,
  getShowCompleteProject,
} from '../BriefSingle.utils';
import { parseRemoteData } from '../../BuyerBriefNew/BuyerBriefNew.utils';

import { MOCK_BRIEF_DATA } from 'mockData/mockBrief';

import { options } from './BuyerSingleBriefLanding.data';

import { useMessageContext } from 'context/MessageContext';

import {
  deleteBuyerFiles,
  downloadBuyerDesignFiles,
  getBriefChatMessages,
  getBuyerBriefProposalDetails,
  getBuyerProposalDetails,
  getCheckoutBrief,
  postBriefChatMessage,
  postBuyerProposalResponse,
  postCancelBriefNegotiation,
  setBuyerCompleteProject,
  uploadBuyerDesignFiles,
} from 'api';

const BuyerSingleBriefLanding = ({ isStory, userType }) => {
  const { brief_uuid, brief_designer_uuid } = useParams();
  const navigate = useNavigate();
  const [setMessage] = useMessageContext();
  const [openCompleteProject, setOpenCompleteProject] = useState(false);
  const [openCongratulations, setOpenCongratulations] = useState(false);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showMessages, setShowMessages] = useState(true);
  const queryClient = useQueryClient();

  const location = useLocation();
  const pathnameArr = location?.pathname.split('/');
  const pathName = pathnameArr[pathnameArr.length - 1];

  const { data, isLoading, isError, isRefetching, refetch } = useQuery(
    ['buyerSingleBrief', brief_uuid, brief_designer_uuid],
    () => getBuyerBriefProposalDetails(brief_uuid, brief_designer_uuid),
    {
      enabled: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  const briefData = useMemo(
    () => (isStory ? MOCK_BRIEF_DATA : (data && data?.brief && parseRemoteData(data?.brief)) || {}),
    [data, isStory]
  );

  const briefName = briefData?.title;
  const chat_uuid = briefData?.chat_uuid || '';
  const brief_status = briefData?.status;
  const designer_name = briefData?.brief_designer?.first_name;
  const company_name = briefData?.company?.name;
  const item_name = briefData?.title;
  const can_cancel_negotiation = briefData?.can_cancel_negotiation;
  const status = briefData?.status;

  const { project_status, proposal_status, agreement_status } = briefData;
  const isProposalTabDisabled = getIsProposalTabDisabled(project_status, proposal_status, brief_status);
  const isAgreementTabDisabled = getIsAgreementTabDisabled(project_status, proposal_status, brief_status);
  const isFilesTabDisabled = getIsFilesTabDisabled(agreement_status, brief_status);

  const showCompleteProject = getShowCompleteProject(agreement_status, project_status);
  const showCheckoutButton = getShowCheckoutButton(agreement_status, project_status);

  const isProjectCompleted = project_status === PROJECT_STATUS.COMPLETED;

  const loading = isLoading || isRefetching;

  const wasDirected = useRef(null);

  const { data: proposalData } = useQuery(
    ['buyerBriefProposalDetails', brief_uuid],
    () => {
      return getBuyerProposalDetails(brief_uuid, brief_designer_uuid);
    },
    {
      enabled: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  useEffect(() => {
    if (!wasDirected.current && proposalData?.proposal && !location.pathname.includes('/success')) {
      wasDirected.current = true;
      navigate(`${routes.briefsDashboard}/${brief_uuid}/${brief_designer_uuid}/proposal`);
    }
  }, [brief_designer_uuid, brief_uuid, navigate, proposalData?.proposal, location.pathname]);

  const handleSectionClick = (path) => {
    navigate(`${routes.briefsDashboard}/${brief_uuid}/${brief_designer_uuid}${path}`);
  };

  const handleOnComplete = async () => {
    const res = await setBuyerCompleteProject(brief_uuid, brief_designer_uuid);
    if (!res.success) {
      setOpenCompleteProject(false);
      return setMessage('There was a problem completing the project');
    }
    setOpenCompleteProject(false);
    setOpenCongratulations(true);
  };

  const handleOnCheckout = async () => {
    if (!isCheckingOut) {
      setIsCheckingOut(true);
      const res = await getCheckoutBrief(brief_uuid, brief_designer_uuid);
      setIsCheckingOut(false);
      if (!res.success || !res.url) {
        return setMessage(res?.data?.error_message || 'There was a problem checking out.');
      }
      // redirect to stripe
      window.location.href = res?.url;
    }
  };

  const handleGetChatMessages = useCallback(() => {
    return getBriefChatMessages(chat_uuid);
  }, [chat_uuid]);

  const handlePostChatMessages = useCallback((chat_uuid, data) => {
    return postBriefChatMessage(chat_uuid, data);
  }, []);

  const handleChatDownloadFiles = useCallback(
    (data) => {
      return downloadBuyerDesignFiles(brief_uuid, brief_designer_uuid, data, true);
    },
    [brief_designer_uuid, brief_uuid]
  );

  const handleChatUploadFiles = useCallback(
    (data) => {
      return uploadBuyerDesignFiles(brief_uuid, brief_designer_uuid, data);
    },
    [brief_designer_uuid, brief_uuid]
  );

  const handleChatDeleteFiles = useCallback(
    (file_uuid) => {
      return deleteBuyerFiles(brief_uuid, brief_designer_uuid, file_uuid);
    },
    [brief_designer_uuid, brief_uuid]
  );

  const toggleMessages = useCallback(() => {
    setShowMessages((prev) => !prev);
  }, []);

  const [openAcceptProposal, setOpenAcceptProposal] = useState(false);
  const [openHaveAccepted, setOpenHaveAccepted] = useState(false);
  const [openRejectProposal, setOpenRejectProposal] = useState(false);

  const onAccept = useCallback(() => {
    setOpenAcceptProposal(true);
  }, []);

  const onReject = useCallback(() => {
    setOpenRejectProposal(true);
  }, []);

  const onCancelNegotiation = useCallback(() => {
    setOpenConfirmCancel(true);
  }, []);

  const handleCloseAcceptedModal = async () => {
    // refetch data or set the proposal to be accepted
    setOpenHaveAccepted(false);
  };

  const navigateToTerms = () => {
    navigate(`${routes.briefsDashboard}/${brief_uuid}/${brief_designer_uuid}/agreement`);
    setOpenHaveAccepted(false);
  };

  const updateProposalCache = useCallback(
    (accept) => {
      queryClient.setQueryData(['buyerBriefProposalDetails', brief_uuid], (oldData) => {
        if (oldData?.proposal) {
          return {
            proposal: {
              ...oldData.proposal,
              status: accept ? PROPOSAL_STATUS.APPROVED : PROPOSAL_STATUS.REJECTED,
            },
            success: true,
          };
        }
      });

      queryClient.setQueryData(['buyerSingleBrief', brief_uuid, brief_designer_uuid], (oldData) => ({
        brief: {
          ...oldData.brief,
          proposal_status: accept ? PROPOSAL_STATUS.APPROVED : PROPOSAL_STATUS.REJECTED,
        },
      }));
    },
    [brief_designer_uuid, brief_uuid, queryClient]
  );

  const handleAcceptSubmit = useCallback(async () => {
    const res = await postBuyerProposalResponse(brief_uuid, brief_designer_uuid, { response: 'accepted' });
    if (!res.success) {
      setMessage('There was an error accepting proposal');
      return setOpenAcceptProposal(false);
    }
    setOpenAcceptProposal(false);
    setOpenHaveAccepted(true);
    updateProposalCache(true);
  }, [brief_designer_uuid, brief_uuid, setMessage, updateProposalCache]);

  const handleRejectSubmit = useCallback(
    async (bool, message) => {
      const res = await postBuyerProposalResponse(brief_uuid, brief_designer_uuid, {
        response: 'rejected',
        rejected_reason: message,
      });
      if (!res.success) {
        setMessage('There was an error rejecting the proposal.');
        return setOpenRejectProposal(false);
      }
      setMessage('You have sucessfully rejected the proposal.');
      updateProposalCache(false);
      setOpenRejectProposal(false);
    },
    [brief_designer_uuid, brief_uuid, setMessage, updateProposalCache]
  );

  const handleCancelNegotiation = useCallback(async () => {
    try {
      const res = await postCancelBriefNegotiation(brief_uuid, brief_designer_uuid);
      if (!res.success) {
        throw res;
      }
      setMessage('You have cancelled this negotiation');

      navigate(`${routes.briefsDashboard}/${brief_uuid}`);
    } catch (err) {
      setMessage('There  was a problem cancelling this negotiation. Please try again');
    }
  }, [brief_uuid, brief_designer_uuid, setMessage, navigate]);

  const showButtons =
    proposal_status === PROPOSAL_STATUS.PENDING_APPROVAL && project_status !== PROJECT_STATUS.REJECTED;

  const briefDesignerName = useMemo(() => {
    const designer = briefData?.brief_designer;
    if (designer) {
      return designer.first_name + ' ' + designer.last_name;
    }
    return '';
  }, [briefData?.brief_designer]);

  return (
    <>
      <BuyerTabSectionsWrapper
        breadcrumbs={
          <Breadcrumbs
            links={[
              { label: 'Briefs', onClick: () => navigate(routes.briefsDashboard) },
              { label: briefName, onClick: () => navigate(`${routes.briefsDashboard}/${brief_uuid}`) },
            ]}
            currentPage={capitalize(pathName || '')}
          />
        }
        title={briefName}
        headerMenuOptions={[
          ...(can_cancel_negotiation
            ? [
                {
                  buttonTitle: 'Cancel Negotiation',
                  onClick: () => onCancelNegotiation(true),
                  icon: <Close fontSize="small" />,
                },
              ]
            : []),
          {
            buttonTitle: showMessages ? 'Hide Messages' : 'Show Messages',
            icon: showMessages ? <SpeakerNotesOffIcon fontSize="small" /> : <ChatIcon fontSize="small" />,
            onClick: toggleMessages,
          },
          {
            buttonTitle: 'Back',
            icon: <ArrowBackIcon fontSize="small" />,
            onClick: () => navigate(`${routes.briefsDashboard}`),
            animate: true,
          },
          ...(showCompleteProject
            ? [
                {
                  buttonTitle: 'Complete Project',
                  icon: <DoneIcon fontSize="small" />,
                  onClick: () => setOpenCompleteProject(true),
                  colour: 'black',
                },
              ]
            : []),
          ...(showCheckoutButton
            ? [
                {
                  buttonTitle: 'Checkout',
                  icon: isCheckingOut ? <Spinner size="20px" /> : <ShoppingCartIcon fontSize="small" />,
                  onClick: () => handleOnCheckout(),
                  colour: 'black',
                },
              ]
            : []),
        ]}
        chatbox={
          <ChatBox
            chat_uuid={chat_uuid}
            setMessage={setMessage}
            getChatMessages={handleGetChatMessages}
            postChatMessage={handlePostChatMessages}
            downloadFiles={handleChatDownloadFiles}
            uploadFiles={handleChatUploadFiles}
            deleteFiles={handleChatDeleteFiles}
            userType={userType}
            expand={showMessages}
            setExpand={toggleMessages}
            onAcceptClick={showButtons ? onAccept : null}
            onRejectClick={showButtons ? onReject : null}
            acceptBtnText={'Connect with designer'}
            rejectBtnText={'Decline Proposal'}
          />
        }
        activeTab={routes.briefsDashboard}
        handleSectionClick={handleSectionClick}
        collapse={showMessages}
        options={options.map((option) => {
          if (option.path === '/proposal') {
            return {
              ...option,
              disabled: isProposalTabDisabled,
            };
          }
          if (option.path === '/agreement') {
            return {
              ...option,
              disabled: isAgreementTabDisabled,
            };
          }
          if (option.path === '/files') {
            return { ...option, disabled: isFilesTabDisabled };
          }
          return option;
        })}
      >
        {loading && (
          <Box mt={2}>
            <Spinner align="center" />
          </Box>
        )}
        {!loading && (
          <>
            <TopBarTitleStatus name={briefDesignerName} project_status={project_status} />
            {!loading && isProjectCompleted && (
              <Box mt={2}>
                <BodyText text="This project has been completed." />
              </Box>
            )}
            <ScrollableContainer collapse={showMessages}>
              <Outlet context={[{ ...briefData }, setMessage, userType, onAccept, onReject]} />
            </ScrollableContainer>
          </>
        )}
        {openCompleteProject && (
          <ModalBriefProposalComplete
            open={openCompleteProject}
            handleClose={() => setOpenCompleteProject(false)}
            onSubmit={handleOnComplete}
          />
        )}
        {openCongratulations && (
          <ModalCongratulations
            handleClose={() => setOpenCongratulations(false)}
            action={() => navigate(`${routes.briefsDashboard}/${brief_uuid}/${brief_designer_uuid}/review`)}
            text="You have completed the project!"
            title="Project"
            open={openCongratulations}
          />
        )}
      </BuyerTabSectionsWrapper>
      {openAcceptProposal && (
        <ModalConfirmAccept
          open={openAcceptProposal}
          name="proposal"
          handleSubmit={handleAcceptSubmit}
          handleClose={() => setOpenAcceptProposal(false)}
          titleOverride="Connect with Designer"
          textOverride={
            "Please confirm you want to connect with this designer. After you've confirmed you can continue to develop the work together."
          }
        />
      )}
      {openConfirmCancel && (
        <ModalConfirmAccept
          open={openConfirmCancel}
          name="proposal"
          handleSubmit={handleCancelNegotiation}
          handleClose={() => setOpenConfirmCancel(false)}
          titleOverride="Cancel Negotiation"
          textOverride={'Please confirm you want to cancel this negotiation.'}
        />
      )}
      {openHaveAccepted && (
        <ModalAcceptProposal
          open={openHaveAccepted}
          handleClose={() => handleCloseAcceptedModal()}
          navigateAction={navigateToTerms}
        />
      )}
      {openRejectProposal && (
        <ModalRejectBrief
          open={openRejectProposal}
          handleClose={() => setOpenRejectProposal(false)}
          handleSubmit={handleRejectSubmit}
          variant="brief-proposal"
          designer_name={designer_name}
          item_name={item_name}
          company_name={company_name}
        />
      )}
    </>
  );
};

BuyerSingleBriefLanding.propTypes = {};

export default BuyerSingleBriefLanding;
